// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useMemo } from 'react';
import { AbstractTriageBrief, PotentialInputSource } from '../../../lib/models';
import { aggregateTriageData } from '../utils/triageAggregator';
import { INPUT_SOURCE_LABELS } from '../constants/triageConstants';

interface AISummaryPanelProps {
  briefs: Record<string, AbstractTriageBrief>;
  papers: Array<{ pmid: string; year?: number }>;
  selectedInputSource: string | null;
  onTagClick: (source: string) => void;
}

export const AISummaryPanel: React.FC<AISummaryPanelProps> = ({
  briefs,
  papers,
  selectedInputSource,
  onTagClick
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const stats = useMemo(() => aggregateTriageData(briefs, papers), [briefs, papers]);

  if (stats.totalAnalyzed === 0) return null;

  // Filter out input sources with 0 counts
  const activeInputSources = (Object.entries(stats.inputSourceCounts) as [PotentialInputSource, number][])
    .filter(([_, count]) => count > 0)
    .sort(([_, a], [__, b]) => b - a);

  const activeRetrievableInputs = Object.entries(stats.retrievableInputCounts)
    .filter(([_, count]) => count > 0)
    .sort(([_, a], [__, b]) => b - a);

  return (
    <div className={`evidence-drawer__ai-summary-panel ${isExpanded ? 'is-expanded' : ''}`}>
      <button 
        className="evidence-drawer__ai-summary-header"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >

        <span className="evidence-drawer__ai-summary-title">AI Findings Overview</span>
        <span className="evidence-drawer__ai-summary-chevron">{isExpanded ? '▼' : '▶'}</span>
      </button>

      {isExpanded && (
        <div className="evidence-drawer__ai-summary-content">
          <div className="evidence-drawer__ai-summary-stats-container">
            <div className="evidence-drawer__ai-summary-stats">
              <div className="evidence-drawer__ai-summary-stat">
                <span className="evidence-drawer__ai-summary-stat-value">{stats.totalAnalyzed}</span>
                <span className="evidence-drawer__ai-summary-stat-label">Papers analyzed</span>
              </div>
              {stats.yearRange && (
                <div className="evidence-drawer__ai-summary-stat">
                  <span className="evidence-drawer__ai-summary-stat-value">
                    {stats.yearRange.min === stats.yearRange.max 
                      ? stats.yearRange.min 
                      : `${stats.yearRange.min}–${stats.yearRange.max}`}
                  </span>
                  <span className="evidence-drawer__ai-summary-stat-label">Year range</span>
                </div>
              )}
            </div>
            {stats.verbalSummary && (
              <div className="evidence-drawer__ai-summary-narrative">
                {stats.verbalSummary}
              </div>
            )}
          </div>

          <div className="evidence-drawer__ai-summary-sections">
            <div className="evidence-drawer__ai-summary-section">
              <h4 className="evidence-drawer__ai-summary-section-title">Model Inputs Covered</h4>
              <div className="evidence-drawer__ai-summary-grid">
                {activeInputSources.map(([source, count]) => (
                  <button 
                    key={source} 
                    className={`evidence-drawer__ai-summary-tag ${selectedInputSource === source ? 'is-active' : ''}`}
                    onClick={() => onTagClick(source)}
                    title={`Show papers matching ${INPUT_SOURCE_LABELS[source] || source}`}
                  >
                    <span className="evidence-drawer__ai-summary-tag-label">
                      {INPUT_SOURCE_LABELS[source] || source}
                    </span>
                    <span className="evidence-drawer__ai-summary-tag-count">{count}</span>
                  </button>
                ))}
              </div>
            </div>

            {activeRetrievableInputs.length > 0 && (
              <div className="evidence-drawer__ai-summary-section">
                <h4 className="evidence-drawer__ai-summary-section-title">Specific Parameters Identified</h4>
                <div className="evidence-drawer__ai-summary-grid">
                  {activeRetrievableInputs.map(([key, count]) => (
                    <div key={key} className="evidence-drawer__ai-summary-tag is-readonly">
                      <span className="evidence-drawer__ai-summary-tag-label">{key}</span>
                      <span className="evidence-drawer__ai-summary-tag-count">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(stats.finlandCounts.high > 0 || stats.finlandCounts.medium > 0) && (
              <div className="evidence-drawer__ai-summary-section">
                <h4 className="evidence-drawer__ai-summary-section-title">Finland Applicability</h4>
                <div className="evidence-drawer__ai-summary-confidence-list">
                  {stats.finlandCounts.high > 0 && (
                    <div className="evidence-drawer__ai-summary-confidence-item confidence--green">
                      High: <span className="evidence-drawer__ai-summary-confidence-count">{stats.finlandCounts.high}</span>
                    </div>
                  )}
                  {stats.finlandCounts.medium > 0 && (
                    <div className="evidence-drawer__ai-summary-confidence-item confidence--blue">
                      Medium: <span className="evidence-drawer__ai-summary-confidence-count">{stats.finlandCounts.medium}</span>
                    </div>
                  )}
                  {stats.finlandCounts.low > 0 && (
                    <div className="evidence-drawer__ai-summary-confidence-item confidence--amber">
                      Low: <span className="evidence-drawer__ai-summary-confidence-count">{stats.finlandCounts.low}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
